import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { useReports } from '../../../../../hooks/api/useReports';
import { getIntl } from '../../../../../utils/intl';
import { useLoadedUser } from '../../../../../hooks/api/useUser';
import useMedicalProfile from '../../../../../hooks/api/useMedicalProfile';
import { Field } from 'formik';
import { handleFormikSubmit } from '../../../../../utils/handleFormikSubmit';
import getName from '../../../../../utils/getName';
import { reportTypeMessages } from '../../../../../utils/sharedMessages';
import { consentCheck } from '../../../../../utils/validators';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { CenteredFormRow, Form } from '../../../../v2BaseComponents/Form';
import { FormStack } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import { FormikInputs } from '../../../../v2BaseComponents/Inputs';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';

const intl = getIntl('components.screens.tabs.profiles.reportsScreen.sharedReports.add', {
    headerText:
        'Shared reports allow you to share a report with anyone.  Reports can be shared via a link or QR code.  Anyone with the link or QR code can view the report.  Caution should be taken when sharing reports as they may contain sensitive information.  Shared reports can always be deleted after creation.  Deleted reports are no longer accessible by anyone even if they have the original link or QR code.',
    reportType: 'Report Type',
    consent:
        "I, {name}, understand that this report will be shared with anyone who has the link or QR code.  Anyone with this link or QR code will be able to see {profileName}'s medical data.",
    create: 'Create',
    paperTitle: 'Create {reportType} Shared Report',
});

export type AddSharedReportScreenOwnProps = {};

export type SharedReportScreenProps = AddSharedReportScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'add-shared-report'>;
};

type FormValue = {
    consentAccepted: boolean;
};

const initialValues: FormValue = {
    consentAccepted: false,
};

const AddSharedReportScreen: React.FC<SharedReportScreenProps> = (props) => {
    const { user } = useLoadedUser({});
    const recordEvent = useRecordEvent();
    const { navigate } = useProfilesTabNavigation();
    const { profile, isLoadingProfile } = useMedicalProfile({ medicalProfileId: props.route.params.profileId });
    const { isCreatingSharedReport, createSharedReport } = useReports({
        medicalProfileId: props.route.params.profileId,
        onCreateSharedReport: (sharedReport) => {
            navigate('shared-report', {
                profileId: props.route.params.profileId,
                sharedReportId: sharedReport.sharedReportId,
            });
        },
    });

    return (
        <AnimateLoadedContent isLoading={isLoadingProfile || !profile || isCreatingSharedReport}>
            {() => (
                <ProfileHeaderContainer profileId={props.route.params.profileId}>
                    <Form
                        title={intl('paperTitle', { reportType: reportTypeMessages(props.route.params.reportType) })}
                        actionContainerSubmit
                        initialValues={initialValues}
                        saveText={intl('create')}
                        onSubmit={(values, helpers) => {
                            const { reportType } = props.route.params;
                            recordEvent('createSharedReport', {
                                reportType,
                            });
                            createSharedReport(reportType);
                            handleFormikSubmit(values, helpers);
                        }}
                    >
                        {({}) => (
                            <FormStack>
                                <CenteredFormRow>
                                    <Text variant="body-1">{intl('headerText')}</Text>
                                </CenteredFormRow>
                                <CenteredFormRow>
                                    <Field
                                        name="consentAccepted"
                                        component={FormikInputs.Checkbox}
                                        title={intl('consent', {
                                            name: getName(user),
                                            profileName: getName(profile!.contactDetails),
                                        })}
                                        topAlignCheckbox
                                        validate={consentCheck}
                                        isDisabled={isCreatingSharedReport}
                                    />
                                </CenteredFormRow>
                            </FormStack>
                        )}
                    </Form>
                </ProfileHeaderContainer>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(AddSharedReportScreen);
